import { LogItem, LogTimeByDateResponse } from "@lhd/types";
import {
  postWorkerLogtimeCreate,
  parseWorkerLogtimeCreateResponseId,
} from "@lhd/hooks";
import {
  buildTrackingTimelineRows,
  findPersistedLogtimeInCycle,
  findUnusedIncompletePersistedLogtime,
  groupDisplayTimelineRowsIntoValidationCycles,
} from "@lhd/features/TrackingTime/timelineRows";
import { toLoggedAtIso } from "./buildSaveFormData";
import { LOG_TYPE } from "@lhd/features/TrackingTime/HomeTracking/contants";

type LogtimePatch = {
  logged_at?: unknown;
  attachments?: unknown[];
};

export const BREAK_END_AUTOFILLED_KEY = "__break_end_autofilled";

const CREATE_ORDER: LogItem["log_type"][] = [
  LOG_TYPE.CHECKIN,
  LOG_TYPE.BREAK_START,
  LOG_TYPE.BREAK_END,
  LOG_TYPE.CHECKOUT,
];

const createOrderIndex = (logType: LogItem["log_type"]): number => {
  const i = CREATE_ORDER.indexOf(logType);
  return i === -1 ? 999 : i;
};

const extractNewFilesFromAttachments = (patch?: LogtimePatch): File[] => {
  if (patch?.attachments == null || !Array.isArray(patch.attachments))
    return [];
  return patch.attachments.filter((a): a is File => a instanceof File);
};

const resolveLoggedAtForRow = (
  lt: LogItem,
  formValues: Record<string, unknown>,
): string | null => {
  const patch = formValues[String(lt.id)] as LogtimePatch | undefined;
  const loggedAt =
    toLoggedAtIso(patch?.logged_at) ??
    (typeof lt.logged_at === "string" && lt.logged_at.trim()
      ? lt.logged_at.trim()
      : "");

  if (typeof loggedAt !== "string" || loggedAt.trim().length === 0) return null;
  return loggedAt.trim();
};

const resolveLoggedAtForValidation = (
  lt: LogItem,
  formValues: Record<string, unknown>,
): string | null => {
  const patch = formValues[String(lt.id)] as LogtimePatch | undefined;
  if (patch != null && typeof patch === "object" && !Array.isArray(patch)) {
    const fromForm = toLoggedAtIso(patch.logged_at);
    if (fromForm) return fromForm;
    if ("logged_at" in patch) return null;
  }
  const server =
    typeof lt.logged_at === "string" && lt.logged_at.trim()
      ? lt.logged_at.trim()
      : "";
  return server.length > 0 ? server : null;
};

const rowHasVirtualUserInput = (
  lt: LogItem,
  formValues: Record<string, unknown>,
): boolean => {
  if (typeof lt.id !== "number" || lt.id >= 0) return false;
  const patch = formValues[String(lt.id)] as LogtimePatch | undefined;
  const files = extractNewFilesFromAttachments(patch);
  const loggedAt = resolveLoggedAtForRow(lt, formValues);
  return !!loggedAt || files.length > 0;
};

export const hasVirtualLogtimesFormInput = (
  data: LogTimeByDateResponse,
  formValues: Record<string, unknown>,
): boolean => {
  const rows = buildTrackingTimelineRows(data);
  return rows.some((lt) => rowHasVirtualUserInput(lt, formValues));
};

export const rowHasVirtualUserTimeInput = (
  lt: LogItem,
  formValues: Record<string, unknown>,
): boolean => {
  if (typeof lt.id !== "number" || lt.id >= 0) return false;
  const patch = formValues[String(lt.id)] as
    | (LogtimePatch & Record<string, unknown>)
    | undefined;
  if (patch?.[BREAK_END_AUTOFILLED_KEY] === true) return false;
  return toLoggedAtIso(patch?.logged_at) != null;
};

const rowNeedsPersistedPreSave = (
  lt: LogItem,
  formValues: Record<string, unknown>,
): boolean => {
  if (typeof lt.id !== "number" || lt.id <= 0) return false;
  const serverMissing =
    lt.logged_at == null ||
    (typeof lt.logged_at === "string" && lt.logged_at.trim() === "");
  if (!serverMissing) return false;
  return resolveLoggedAtForRow(lt, formValues) != null;
};

export const shouldPersistSaveBeforeVirtualCreates = (
  data: LogTimeByDateResponse,
  formValues: Record<string, unknown>,
  canEditAllAttendanceFields: boolean,
): boolean => {
  if (!canEditAllAttendanceFields) return false;

  const rows = buildTrackingTimelineRows(data);
  const virtualWithInput = rows.filter((lt) =>
    rowHasVirtualUserInput(lt, formValues),
  );
  if (virtualWithInput.length === 0) return false;

  const persistedNeedingPreSave = rows.filter((lt) =>
    rowNeedsPersistedPreSave(lt, formValues),
  );
  if (persistedNeedingPreSave.length === 0) return false;

  const wouldBreakLifecycleOrder = virtualWithInput.some((v) =>
    persistedNeedingPreSave.some(
      (p) => createOrderIndex(v.log_type) < createOrderIndex(p.log_type),
    ),
  );
  if (wouldBreakLifecycleOrder) return false;

  return true;
};

export const applyImplicitBreakEndToFormValues = (
  data: LogTimeByDateResponse,
  formValues: Record<string, unknown>,
): void => {
  const rows = buildTrackingTimelineRows(data);
  autofillBreakEndWithCheckout(rows, formValues);
  clampBreakTimesIntoCycleWindows(rows, formValues);
};

const patchRowLoggedAt = (
  rowId: number,
  loggedAt: string,
  formValues: Record<string, unknown>,
): void => {
  const key = String(rowId);
  const patch =
    formValues[key] != null &&
    typeof formValues[key] === "object" &&
    !Array.isArray(formValues[key])
      ? (formValues[key] as Record<string, unknown>)
      : {};
  formValues[key] = {
    ...patch,
    logged_at: loggedAt,
  };
};

const clampBreakTimesIntoCycleWindows = (
  rows: LogItem[],
  formValues: Record<string, unknown>,
): void => {
  const cycles = groupDisplayTimelineRowsIntoValidationCycles(rows);

  for (const cycle of cycles) {
    const checkin = cycle.find((row) => row.log_type === LOG_TYPE.CHECKIN);
    const checkout = cycle.find((row) => row.log_type === LOG_TYPE.CHECKOUT);
    const breakStartRows = cycle.filter(
      (row) => row.log_type === LOG_TYPE.BREAK_START,
    );
    const breakEndRows = cycle.filter(
      (row) => row.log_type === LOG_TYPE.BREAK_END,
    );

    const checkinIso = checkin
      ? resolveLoggedAtForRow(checkin, formValues)
      : null;
    if (checkinIso == null) continue;

    const checkinMs = Date.parse(checkinIso);
    if (!Number.isFinite(checkinMs)) continue;

    const checkoutIso = checkout
      ? resolveLoggedAtForRow(checkout, formValues)
      : null;
    const checkoutMs =
      checkoutIso != null ? Date.parse(checkoutIso) : Number.NaN;

    for (const breakStartRow of breakStartRows) {
      const iso = resolveLoggedAtForRow(breakStartRow, formValues);
      if (!iso) continue;
      const ms = Date.parse(iso);
      if (Number.isFinite(ms) && ms < checkinMs) {
        patchRowLoggedAt(breakStartRow.id, checkinIso, formValues);
      } else if (
        Number.isFinite(ms) &&
        Number.isFinite(checkoutMs) &&
        checkoutIso != null &&
        ms > checkoutMs
      ) {
        patchRowLoggedAt(breakStartRow.id, checkoutIso, formValues);
      }
    }

    if (Number.isFinite(checkoutMs) && checkoutIso) {
      for (const breakEndRow of breakEndRows) {
        const iso = resolveLoggedAtForRow(breakEndRow, formValues);
        if (!iso) continue;
        const ms = Date.parse(iso);
        if (Number.isFinite(ms) && ms > checkoutMs) {
          patchRowLoggedAt(breakEndRow.id, checkoutIso, formValues);
        }
      }
    }
  }
};

const autofillBreakEndWithCheckout = (
  rows: LogItem[],
  formValues: Record<string, unknown>,
): void => {
  const rowsAsc = [...rows].reverse();
  let currentBreakStartMs: number | null = null;
  let pendingBreakEndRowId: number | null = null;

  for (const row of rowsAsc) {
    const loggedAt = resolveLoggedAtForRow(row, formValues);

    switch (row.log_type) {
      case LOG_TYPE.CHECKIN: {
        currentBreakStartMs = null;
        pendingBreakEndRowId = null;
        break;
      }
      case LOG_TYPE.BREAK_START: {
        if (!loggedAt) break;
        const ms = Date.parse(loggedAt);
        if (!Number.isFinite(ms)) break;
        currentBreakStartMs = ms;
        break;
      }
      case LOG_TYPE.BREAK_END: {
        if (!loggedAt) {
          if (currentBreakStartMs != null) {
            pendingBreakEndRowId = row.id;
          }
          break;
        }
        const ms = Date.parse(loggedAt);
        if (!Number.isFinite(ms)) break;
        if (currentBreakStartMs != null && ms > currentBreakStartMs) {
          currentBreakStartMs = null;
          pendingBreakEndRowId = null;
        }
        break;
      }
      case LOG_TYPE.CHECKOUT: {
        if (
          currentBreakStartMs == null ||
          pendingBreakEndRowId == null ||
          !loggedAt
        ) {
          break;
        }
        const key = String(pendingBreakEndRowId);
        const patch =
          formValues[key] != null &&
          typeof formValues[key] === "object" &&
          !Array.isArray(formValues[key])
            ? (formValues[key] as Record<string, unknown>)
            : {};
        formValues[key] = {
          ...patch,
          logged_at: loggedAt,
          [BREAK_END_AUTOFILLED_KEY]: true,
        };
        currentBreakStartMs = null;
        pendingBreakEndRowId = null;
        break;
      }
      default:
        break;
    }
  }
};

type ResolvedTimelineRow = {
  id: number;
  log_type: LogItem["log_type"];
  loggedAtMs: number;
};

export class TimelineValidationError extends Error {
  readonly messages: readonly string[];

  constructor(messages: string[]) {
    const unique = Array.from(new Set(messages.filter(Boolean)));
    super(unique.join("\n"));
    this.name = "TimelineValidationError";
    this.messages = unique;
  }
}

const VALIDATION_MSG = {
  CHECKIN_REQUIRED: "出勤時間を入力してください。",
  CHECKOUT_REQUIRED: "退勤時間を入力してください。",
  BREAK_START_REQUIRED: "休憩開始時間を入力してください。",
  BREAK_END_REQUIRED: "休憩終了時間を入力してください。",
  ORDER_INVALID: "打刻時間の順序が正しくありません。",
  INVALID_FORMAT: "入力した時刻の形式が不正です。",
} as const;

const MISSING_FIELD_MSG_ORDER = [
  VALIDATION_MSG.CHECKOUT_REQUIRED,
  VALIDATION_MSG.BREAK_END_REQUIRED,
  VALIDATION_MSG.BREAK_START_REQUIRED,
  VALIDATION_MSG.CHECKIN_REQUIRED,
];

const allResolvedRowsSameMinute = (
  resolvedRows: ResolvedTimelineRow[],
): boolean =>
  resolvedRows.length > 0 &&
  resolvedRows.every((row) => row.loggedAtMs === resolvedRows[0]!.loggedAtMs);

const sliceHasBreakEndRequiringStart = (
  slice: LogItem[],
  formValues: Record<string, unknown>,
): boolean =>
  slice.some((row) => {
    if (row.log_type !== LOG_TYPE.BREAK_END) return false;
    if (resolveLoggedAtForValidation(row, formValues) == null) return false;
    const patch = formValues[String(row.id)] as
      | (LogtimePatch & Record<string, unknown>)
      | undefined;
    if (
      patch?.[BREAK_END_AUTOFILLED_KEY] === true &&
      !sliceHasTimeForType(slice, LOG_TYPE.BREAK_START, formValues)
    ) {
      return false;
    }
    return true;
  });

const sliceHasTimeForType = (
  slice: LogItem[],
  logType: LogItem["log_type"],
  formValues: Record<string, unknown>,
): boolean =>
  slice.some(
    (row) =>
      row.log_type === logType &&
      resolveLoggedAtForValidation(row, formValues) != null,
  );

type SliceTimePresence = {
  hasCheckin: boolean;
  hasCheckout: boolean;
  hasBreakStart: boolean;
  hasBreakEnd: boolean;
};

const getSliceTimePresence = (
  slice: LogItem[],
  formValues: Record<string, unknown>,
): SliceTimePresence => ({
  hasCheckin: sliceHasTimeForType(slice, LOG_TYPE.CHECKIN, formValues),
  hasCheckout: sliceHasTimeForType(slice, LOG_TYPE.CHECKOUT, formValues),
  hasBreakStart: sliceHasTimeForType(slice, LOG_TYPE.BREAK_START, formValues),
  hasBreakEnd: sliceHasTimeForType(slice, LOG_TYPE.BREAK_END, formValues),
});

const collectMissingFieldErrorsForCycles = (
  cycles: LogItem[][],
  formValues: Record<string, unknown>,
): string[] => {
  const errors = new Set<string>();

  for (const slice of cycles) {
    const { hasCheckin, hasCheckout, hasBreakStart, hasBreakEnd } =
      getSliceTimePresence(slice, formValues);

    if (!hasCheckin) {
      if (hasCheckout || hasBreakStart || hasBreakEnd) {
        errors.add(VALIDATION_MSG.CHECKIN_REQUIRED);
      }
      continue;
    }

    if (!hasCheckout) {
      errors.add(VALIDATION_MSG.CHECKOUT_REQUIRED);
    }
    if (hasBreakStart && !hasBreakEnd) {
      errors.add(VALIDATION_MSG.BREAK_END_REQUIRED);
    }
    if (sliceHasBreakEndRequiringStart(slice, formValues) && !hasBreakStart) {
      errors.add(VALIDATION_MSG.BREAK_START_REQUIRED);
    }
  }

  return MISSING_FIELD_MSG_ORDER.filter((msg) => errors.has(msg));
};

const collectMissingFieldErrors = (
  rows: LogItem[],
  formValues: Record<string, unknown>,
): string[] =>
  collectMissingFieldErrorsForCycles(
    groupDisplayTimelineRowsIntoValidationCycles(rows),
    formValues,
  );

const shouldValidateSliceTimeOrder = (
  hasCheckin: boolean,
  hasCheckout: boolean,
  hasBreakStart: boolean,
  hasBreakEnd: boolean,
): boolean => {
  if (!hasCheckin) return false;
  return hasCheckout || hasBreakStart || hasBreakEnd;
};

const resolveCycleTimelineRows = (
  cycle: LogItem[],
  formValues: Record<string, unknown>,
): ResolvedTimelineRow[] =>
  cycle
    .map((row, cycleIndex) => {
      const loggedAt = resolveLoggedAtForValidation(row, formValues);
      if (!loggedAt) return null;
      const ms = Date.parse(loggedAt);
      if (!Number.isFinite(ms)) {
        throw new TimelineValidationError([VALIDATION_MSG.INVALID_FORMAT]);
      }
      const msMinute = Math.floor(ms / 60_000) * 60_000;
      return {
        id: row.id,
        log_type: row.log_type,
        loggedAtMs: msMinute,
        cycleIndex,
      };
    })
    .filter((r): r is ResolvedTimelineRow & { cycleIndex: number } => r != null)
    .sort((a, b) => {
      if (a.loggedAtMs !== b.loggedAtMs) return a.loggedAtMs - b.loggedAtMs;
      return a.cycleIndex - b.cycleIndex;
    })
    .map(({ cycleIndex: _cycleIndex, ...row }) => row);

const isSliceBreakPairOrderInvalid = (
  slice: LogItem[],
  formValues: Record<string, unknown>,
): boolean => {
  let breakStartMs: number | null = null;
  let breakEndMs: number | null = null;

  for (const row of slice) {
    const loggedAt = resolveLoggedAtForValidation(row, formValues);
    if (!loggedAt) continue;
    const ms = Date.parse(loggedAt);
    if (!Number.isFinite(ms)) continue;
    const msMinute = Math.floor(ms / 60_000) * 60_000;
    if (row.log_type === LOG_TYPE.BREAK_START) {
      breakStartMs = msMinute;
    } else if (row.log_type === LOG_TYPE.BREAK_END) {
      breakEndMs = msMinute;
    }
  }

  return (
    breakStartMs != null && breakEndMs != null && breakEndMs < breakStartMs
  );
};

const isTimelineOrderInvalid = (
  resolvedRows: ResolvedTimelineRow[],
): boolean => {
  if (allResolvedRowsSameMinute(resolvedRows)) return false;

  let isInsideCycle = false;
  let currentCheckinMs: number | null = null;
  let currentBreakStartMs: number | null = null;
  let lastCheckInOutMs: number | null = null;

  for (const row of resolvedRows) {
    const t = row.loggedAtMs;
    switch (row.log_type) {
      case LOG_TYPE.CHECKIN: {
        if (isInsideCycle) return true;
        if (lastCheckInOutMs != null && t <= lastCheckInOutMs) return true;
        isInsideCycle = true;
        currentCheckinMs = t;
        currentBreakStartMs = null;
        lastCheckInOutMs = t;
        break;
      }
      case LOG_TYPE.BREAK_START: {
        if (!isInsideCycle || currentCheckinMs == null) return true;
        if (currentBreakStartMs != null) return true;
        if (t < currentCheckinMs) return true;
        currentBreakStartMs = t;
        break;
      }
      case LOG_TYPE.BREAK_END: {
        if (!isInsideCycle || currentCheckinMs == null) return true;
        if (currentBreakStartMs == null) {
          if (t < currentCheckinMs) return true;
          const laterBreakStart = resolvedRows.find(
            (r) => r.log_type === LOG_TYPE.BREAK_START && r.loggedAtMs > t,
          );
          if (laterBreakStart != null) return true;
          break;
        }
        if (t < currentBreakStartMs) return true;
        currentBreakStartMs = null;
        break;
      }
      case LOG_TYPE.CHECKOUT: {
        if (!isInsideCycle || currentCheckinMs == null) return true;
        if (t < currentCheckinMs) return true;
        if (lastCheckInOutMs != null && t < lastCheckInOutMs) return true;
        isInsideCycle = false;
        currentCheckinMs = null;
        currentBreakStartMs = null;
        lastCheckInOutMs = t;
        break;
      }
      default:
        break;
    }
  }

  return false;
};

const validateTimelineRows = (
  rows: LogItem[],
  formValues: Record<string, unknown>,
): void => {
  const cycles = groupDisplayTimelineRowsIntoValidationCycles(rows);
  const validationErrors = [
    ...collectMissingFieldErrorsForCycles(cycles, formValues),
  ];

  for (const slice of cycles) {
    const { hasCheckin, hasCheckout, hasBreakStart, hasBreakEnd } =
      getSliceTimePresence(slice, formValues);

    if (
      !shouldValidateSliceTimeOrder(
        hasCheckin,
        hasCheckout,
        hasBreakStart,
        hasBreakEnd,
      )
    ) {
      continue;
    }

    if (isSliceBreakPairOrderInvalid(slice, formValues)) {
      validationErrors.push(VALIDATION_MSG.ORDER_INVALID);
      break;
    }

    const sliceResolved = resolveCycleTimelineRows(slice, formValues);
    if (sliceResolved.length > 0 && isTimelineOrderInvalid(sliceResolved)) {
      validationErrors.push(VALIDATION_MSG.ORDER_INVALID);
      break;
    }
  }

  if (validationErrors.length > 0) {
    throw new TimelineValidationError(validationErrors);
  }
};

export function validateTrackingTimelineForm(
  data: LogTimeByDateResponse,
  formValues: Record<string, unknown>,
): void {
  const rows = buildTrackingTimelineRows(data);
  autofillBreakEndWithCheckout(rows, formValues);
  validateTimelineRows(rows, formValues);
}

export function syncFormAttachmentsAfterWorkerLogtimeCreate(
  data: LogTimeByDateResponse,
  formValues: Record<string, unknown>,
  idMap: Map<number, number>,
): Record<string, unknown> {
  if (idMap.size === 0) return formValues;

  const next = { ...formValues };
  idMap.forEach((realId) => {
    const lt = (data.logtimes ?? []).find((row) => row.id === realId);
    if (lt == null) return;
    if (lt.log_type !== LOG_TYPE.CHECKIN && lt.log_type !== LOG_TYPE.CHECKOUT) {
      return;
    }

    const key = String(realId);
    const patch = next[key];
    const patchObj =
      patch != null && typeof patch === "object" && !Array.isArray(patch)
        ? (patch as Record<string, unknown>)
        : {};
    const patchAttachments = patchObj.attachments;
    const hadPendingFiles =
      Array.isArray(patchAttachments) &&
      patchAttachments.some((att) => att instanceof File);
    const serverAttachments = lt.attachments ?? [];
    if (!hadPendingFiles || serverAttachments.length === 0) return;

    next[key] = {
      ...patchObj,
      attachments: serverAttachments,
    };
  });

  return next;
}

export function remapFormKeysAfterCreate(
  formValues: Record<string, unknown>,
  idMap: Map<number, number>,
): Record<string, unknown> {
  const next = { ...formValues };
  idMap.forEach((realId, virtualId) => {
    const vs = String(virtualId);
    const rs = String(realId);
    const patch = next[vs];
    if (patch == null) return;
    const existing = next[rs];
    next[rs] =
      existing != null &&
      typeof existing === "object" &&
      !Array.isArray(existing) &&
      patch != null &&
      typeof patch === "object" &&
      !Array.isArray(patch)
        ? {
            ...(existing as Record<string, unknown>),
            ...(patch as Record<string, unknown>),
          }
        : patch;
    delete next[vs];
  });
  return next;
}

export async function createVirtualLogtimesBeforeSave(options: {
  data: LogTimeByDateResponse;
  formValues: Record<string, unknown>;
  workDate: string;
  workerId: number;
  companyId: string;
  canEditAllAttendanceFields: boolean;
}): Promise<Map<number, number>> {
  const {
    data,
    formValues,
    workDate,
    workerId,
    companyId,
    canEditAllAttendanceFields,
  } = options;
  const rows = buildTrackingTimelineRows(data);
  const hadNoLogtimesAtStart = (data.logtimes ?? []).length === 0;
  if (canEditAllAttendanceFields) {
    validateTrackingTimelineForm(data, formValues);
  }

  const toCreate: {
    virtualId: number;
    logType: LogItem["log_type"];
    loggedAt: string | null;
    files: File[];
  }[] = [];
  const idMap = new Map<number, number>();
  const reusedPersistedIds = new Set<number>();
  const cycles = groupDisplayTimelineRowsIntoValidationCycles(rows);
  const cycleTargetByKey = new Map<string, number>();

  for (const lt of rows) {
    if (typeof lt.id !== "number" || lt.id >= 0) continue;

    const cycleIndex = cycles.findIndex((cycle) =>
      cycle.some((row) => row.id === lt.id),
    );
    const dedupeKey =
      cycleIndex >= 0
        ? `${cycleIndex}:${lt.log_type}`
        : `orphan:${lt.id}:${lt.log_type}`;

    const mappedTarget = cycleTargetByKey.get(dedupeKey);
    if (mappedTarget != null) {
      idMap.set(lt.id, mappedTarget);
      continue;
    }

    const patch = formValues[String(lt.id)] as LogtimePatch | undefined;
    const files = extractNewFilesFromAttachments(patch);
    const loggedAt = resolveLoggedAtForRow(lt, formValues);
    if (!loggedAt && files.length === 0) continue;

    const cycle = cycleIndex >= 0 ? cycles[cycleIndex] : undefined;
    const reusable =
      cycle != null
        ? findPersistedLogtimeInCycle(cycle, lt.log_type, reusedPersistedIds)
        : findUnusedIncompletePersistedLogtime(
            data.logtimes,
            lt.log_type,
            reusedPersistedIds,
          );
    if (reusable != null) {
      reusedPersistedIds.add(reusable.id);
      idMap.set(lt.id, reusable.id);
      cycleTargetByKey.set(dedupeKey, reusable.id);
      continue;
    }

    toCreate.push({
      virtualId: lt.id,
      logType: lt.log_type,
      loggedAt,
      files,
    });
  }

  toCreate.sort(
    (a, b) => createOrderIndex(a.logType) - createOrderIndex(b.logType),
  );

  const dayMemoForCreate = String(formValues.memo ?? "").trim();
  const hasAttachmentOnLifecycleCreates = toCreate.some(
    (item) =>
      item.files.length > 0 &&
      (item.logType === LOG_TYPE.CHECKIN || item.logType === LOG_TYPE.CHECKOUT),
  );
  const shouldCreateStandaloneNote =
    hadNoLogtimesAtStart &&
    (dayMemoForCreate !== "" || hasAttachmentOnLifecycleCreates);

  for (const item of toCreate) {
    const isLimitedUser = !canEditAllAttendanceFields;
    const hasLoggedAt =
      item.loggedAt != null && String(item.loggedAt).trim() !== "";
    const sendEditLogtimeAt =
      isLimitedUser || (item.files.length > 0 && !hasLoggedAt);
    const res = await postWorkerLogtimeCreate({
      company_id: companyId,
      worker_id: workerId,
      work_date: workDate,
      log_type: item.logType,
      logged_at: isLimitedUser ? null : item.loggedAt,
      ...(sendEditLogtimeAt ? { edit_logtime_at: true } : {}),
      ...(dayMemoForCreate !== "" ? { note: dayMemoForCreate } : {}),
      files: item.files,
    });
    const newId = parseWorkerLogtimeCreateResponseId(res);
    if (newId == null) {
      throw new Error("作成された打刻のIDを取得できませんでした。");
    }
    idMap.set(item.virtualId, newId);
    const itemCycleIndex = cycles.findIndex((cycle) =>
      cycle.some((row) => row.id === item.virtualId),
    );
    if (itemCycleIndex >= 0) {
      cycleTargetByKey.set(`${itemCycleIndex}:${item.logType}`, newId);
    }
  }

  for (const lt of rows) {
    if (typeof lt.id !== "number" || lt.id >= 0 || idMap.has(lt.id)) continue;
    const cycleIndex = cycles.findIndex((cycle) =>
      cycle.some((row) => row.id === lt.id),
    );
    if (cycleIndex < 0) continue;
    const dedupeKey = `${cycleIndex}:${lt.log_type}`;
    const mappedTarget = cycleTargetByKey.get(dedupeKey);
    if (mappedTarget != null) {
      idMap.set(lt.id, mappedTarget);
    }
  }

  if (shouldCreateStandaloneNote) {
    await postWorkerLogtimeCreate({
      company_id: companyId,
      worker_id: workerId,
      work_date: workDate,
      log_type: LOG_TYPE.NOTE,
      ...(dayMemoForCreate !== "" ? { note: dayMemoForCreate } : {}),
      files: [],
    });
  }

  return idMap;
}
